'use client';
import React, { useState, useRef, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { IconMap } from '../IconMap';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { cn } from '@workspace/lib';

// Dropdown
const dropdownButton = cva(
  'flex items-center w-full rounded px-3 py-2 h-11 bg-white text-left focus:outline-none font-openSans transition',
  {
    variants: {
      status: {
        default:
          'border-secondary-100 hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)]',
        error:
          'border-error hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)] focus-within:!border-sm !border-md',
        success:
          'border-success hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-[0_0_0_2px_white,0_0_0_4px_theme(colors.primary.500)]',
      },
    },
    defaultVariants: {
      status: 'default',
    },
  },
);

export interface DropdownOption {
  id?: string;
  label: string;
  value: string;
  icon?: keyof typeof IconMap | React.ReactNode;
  disabled?: boolean;
}

export interface DropdownProps {
  label: string;
  hint?: string;
  placeholder: string;
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  labelClassName?: string;
  status?: 'default' | 'error' | 'success';
}

export function Dropdown({
  label,
  hint,
  placeholder,
  options,
  value,
  onChange,
  className,
  labelClassName,
  status = 'default',
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  const iconColorClass = {
    default: 'text-secondary-400',
    error: 'text-error group-focus-within:text-secondary-400',
    success: 'text-success',
  }[status];

  return (
    <div className={cn('w-56 relative group', className)} ref={ref}>
      <Label text={label} className={cn('text-sm font-medium text-gray-700 flex items-center gap-1', labelClassName)} />
      {hint && <div className="text-xs text-secondary-400 mb-1">{hint}</div>}
      <div className="relative">
        <button
          type="button"
          className={cn(dropdownButton({ status }), 'border')}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selectedOption?.icon && <span className="mr-2">{selectedOption.icon}</span>}
          <span className={selectedOption ? 'text-secondary-300' : 'text-secondary-200'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className={cn('ml-auto transition-transform duration-200', iconColorClass, open && 'rotate-180')}>
            <Icon name="chevronDownSmallIcon" size={24} />
          </span>
        </button>
      </div>
      {open && (
        <ul
          className="absolute z-10 mt-1 bg-white border border-secondary-200 rounded shadow-lg max-h-60 overflow-auto w-full"
          role="listbox"
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              className={`flex items-center px-3 py-2 cursor-pointer select-none transition hover:bg-secondary-50 ${opt.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (!opt.disabled) {
                  onChange(opt.value);
                  setOpen(false);
                }
              }}
            >
              {opt.icon && (
                <span className="mr-2">
                  {typeof opt.icon === 'string' ? <Icon name={opt.icon as keyof typeof IconMap} size={18} /> : opt.icon}
                </span>
              )}
              <span className="flex-1 font-openSans">{opt.label}</span>
              {opt.disabled && (
                <span className="ml-2 text-secondary-400">
                  <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                    <path
                      d="M4 8a4 4 0 118 0 4 4 0 01-8 0zm8 0H4"
                      stroke="#888"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
